import { useRef, useState, forwardRef, useImperativeHandle } from 'react';

import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { IconButtonStyled, TooltipStyled } from './CopyButton.styles';

const DEFAULT_DISPLAY_TIME = 2000;

const CopyButton = forwardRef(function CopyButton(
  {
    copyText,
    message = 'Copied!',
    children,
    iconSize = 'small',
    iconColor = 'primary',
    placement = 'right',
    callback,
    arrow
  },
  ref
) {
  const [showTooltip, setShowTooltip] = useState(false);
  const copyButtonRef = useRef();

  const handleShowTooltip = () => {
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, DEFAULT_DISPLAY_TIME);
  };

  const handleClick = () => {
    copy(copyText);
    copyButtonRef.current.blur();
    if (typeof callback === 'function') {
      callback();
    }
    handleShowTooltip();
  };

  useImperativeHandle(
    ref,
    () => ({
      copyAndShowTooltip() {
        if (typeof callback === 'function') {
          callback();
        }
        copy(copyText);
        handleShowTooltip();
      },
      showTooltip() {
        handleShowTooltip();
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [copyText]
  );

  return (
    <TooltipStyled
      arrow={arrow}
      open={showTooltip}
      placement={placement}
      title={message}
    >
      {children ?? (
        <IconButtonStyled
          label="copy button"
          onClick={handleClick}
          ref={copyButtonRef}
          size="sm"
        >
          <ContentCopyIcon
            color={iconColor}
            fontSize={iconSize}
          />
        </IconButtonStyled>
      )}
    </TooltipStyled>
  );
});

export default CopyButton;

CopyButton.propTypes = {
  copyText: PropTypes.string,
  children: PropTypes.node,
  message: PropTypes.string,
  iconSize: PropTypes.oneOf(['small', 'inherit', 'large', 'medium']),
  iconColor: PropTypes.string,
  placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  callback: PropTypes.func,
  arrow: PropTypes.bool
};
